import { Copy, MessageSquareText, Send, Trash2, UserRound } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useSearch } from 'wouter';
import ContextLibrary from '../components/ContextLibrary';
import VoiceCastDialog from '../components/VoiceCastDialog';
import WaveIcon from '../components/WaveIcon';
import { createProject, deleteProject, duplicateProject, fetchProject, fetchProjectShares, saveSessionConfig, sendSetupMessage, shareProject, updateProjectContext, updateProjectTitle, updateProjectVoiceCast } from '../lib/api';
import { useAuth } from '../lib/auth';
import { trackJourney } from '../lib/journey';
import { appRedirectState } from '../lib/navigation';
import type { InterviewConfig, PracticeMode, ProjectDetail, ProjectShare, SessionSummary, VoiceCast } from '../types';

const modes = [
  { id: 'text' as const, title: 'Text', detail: 'Rehearse the conversation in writing', icon: MessageSquareText, tone: 'sand' },
  { id: 'voice' as const, title: 'Voice', detail: 'Speak aloud with adaptive follow-ups', icon: WaveIcon, tone: 'blue' },
  { id: 'avatar' as const, title: 'Avatar', detail: 'Practice with a visible participant', icon: UserRound, tone: 'coral' },
];

const projectTabs = ['setup', 'sessions', 'settings'] as const;
type ProjectTab = typeof projectTabs[number];

function resolveTab(tab?: string): ProjectTab {
  return projectTabs.includes(tab as ProjectTab) ? tab as ProjectTab : 'setup';
}

function SessionIcon({ mode }: { mode: PracticeMode }) {
  if (mode === 'text') return <MessageSquareText size={17} />;
  if (mode === 'voice') return <WaveIcon size={17} />;
  return <UserRound size={17} />;
}

function sessionTimestamp(createdAt: string) {
  return new Date(createdAt).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function sessionDate(createdAt: string) {
  return new Date(createdAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function practiceTime(totalMinutes: number) {
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const remainder = totalMinutes % 60;
  return remainder ? `${Math.floor(totalMinutes / 60)}h ${remainder}m` : `${Math.floor(totalMinutes / 60)}h`;
}

export default function NewPractice({ projectId, tab }: { projectId?: string; tab?: string }) {
  const [, navigate] = useLocation();
  const search = useSearch();
  const searchParams = new URLSearchParams(search);
  const { getToken, user } = useAuth();
  const viewedEventId = useRef(crypto.randomUUID());
  const newProjectId = useRef(crypto.randomUUID());
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const cancelTitleSave = useRef(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const contextQueue = useRef<Promise<unknown>>(Promise.resolve());
  const contextOperations = useRef(0);
  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [draft, setDraft] = useState('');
  const [titleDraft, setTitleDraft] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [voiceCastOpen, setVoiceCastOpen] = useState(false);
  const [modeSheetOpen, setModeSheetOpen] = useState(false);
  const [attachmentsPending, setAttachmentsPending] = useState(false);
  const [contextSaving, setContextSaving] = useState(false);
  const [contextChipsContainer, setContextChipsContainer] = useState<HTMLDivElement | null>(null);
  const [error, setError] = useState('');
  const [renameDraft, setRenameDraft] = useState('');
  const [shares, setShares] = useState<ProjectShare[]>([]);
  const [shareEmail, setShareEmail] = useState('');
  const [shareAccessRole, setShareAccessRole] = useState<'viewer' | 'editor'>('viewer');
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const activeTab = resolveTab(tab);
  const currentProjectId = detail?.project.id;
  const accessRole = detail?.project.accessRole;

  useEffect(() => {
    void trackJourney(getToken, 'practice_setup_viewed', { clientEventId: viewedEventId.current });
  }, [getToken]);

  useEffect(() => {
    let active = true;
    setDetail(null);
    setLoading(true);
    const load = async () => {
      const token = await getToken();
      if (projectId) return fetchProject(token, projectId);
      const initialParams = new URLSearchParams(search);
      return createProject(token, {
        id: newProjectId.current,
        initialPrompt: initialParams.get('role') || undefined,
        fromSessionId: initialParams.get('fromSession') || undefined,
      });
    };
    void load().then((loaded) => {
      if (!active) return;
      setDetail(loaded);
      setRenameDraft(loaded.project.title);
      setLoading(false);
      window.dispatchEvent(new Event('maxed:project-saved'));
      if (!projectId) navigate(`/projects/${loaded.project.id}`, { replace: true, state: appRedirectState });
    }).catch((cause: unknown) => {
      if (!active) return;
      setError(cause instanceof Error ? cause.message : 'Could not load this project.');
      setLoading(false);
    });
    return () => { active = false; };
  }, [getToken, navigate, projectId, search]);

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
  }, [detail?.messages, sending, activeTab]);

  useEffect(() => {
    if (activeTab !== 'settings' || !currentProjectId || accessRole !== 'owner') return;
    let active = true;
    void getToken().then((token) => fetchProjectShares(token, currentProjectId)).then(({ shares: loaded }) => {
      if (active) setShares(loaded);
    }).catch((cause: unknown) => {
      if (active) setShareError(cause instanceof Error ? cause.message : 'Could not load project members.');
    });
    return () => { active = false; };
  }, [accessRole, activeTab, currentProjectId, getToken]);

  useEffect(() => {
    if (!modeSheetOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') setModeSheetOpen(false); };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [modeSheetOpen]);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = '0px';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    textarea.style.overflowY = textarea.scrollHeight > 150 ? 'auto' : 'hidden';
  }, [draft, activeTab]);

  useLayoutEffect(() => {
    const input = titleInputRef.current;
    if (!editingTitle || !input) return;
    input.style.width = '1px';
    const available = input.parentElement?.clientWidth || 800;
    input.style.width = `${Math.min(Math.max(24, input.scrollWidth + 2), available)}px`;
  }, [editingTitle, titleDraft]);

  function setContextIds(contextIds: string[]) {
    if (!detail) return;
    setDetail((current) => current ? { ...current, project: { ...current.project, contextIds } } : current);
    contextOperations.current += 1;
    setContextSaving(true);
    const id = detail.project.id;
    const operation = contextQueue.current.catch(() => undefined).then(async () => {
      const token = await getToken();
      const updated = await updateProjectContext(token, id, contextIds);
      setDetail(updated);
      setError('');
      window.dispatchEvent(new Event('maxed:project-saved'));
    });
    contextQueue.current = operation;
    void operation.catch(async (cause: unknown) => {
      setError(cause instanceof Error ? cause.message : 'Could not save the attached materials.');
      try {
        const token = await getToken();
        setDetail(await fetchProject(token, id));
      } catch {
        // Keep the original persistence error visible.
      }
    }).finally(() => {
      contextOperations.current -= 1;
      if (!contextOperations.current) setContextSaving(false);
    });
  }

  function insertAtCursor(text: string) {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? draft.length;
    const end = textarea?.selectionEnd ?? start;
    const nextCursor = start + text.length;
    setDraft(`${draft.slice(0, start)}${text}${draft.slice(end)}`);
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(nextCursor, nextCursor);
    });
  }

  function editTitle() {
    if (!detail || detail.project.accessRole === 'viewer') return;
    cancelTitleSave.current = false;
    setTitleDraft(detail.project.title);
    setEditingTitle(true);
    requestAnimationFrame(() => titleInputRef.current?.select());
  }

  async function commitTitle(nextTitle: string) {
    if (!detail) return;
    const title = nextTitle.replace(/\s+/g, ' ').trim();
    if (!title || title === detail.project.title) {
      setRenameDraft(detail.project.title);
      return;
    }
    const previousTitle = detail.project.title;
    setDetail((current) => current ? { ...current, project: { ...current.project, title } } : current);
    setRenameDraft(title);
    setError('');
    try {
      const token = await getToken();
      await updateProjectTitle(token, detail.project.id, title);
      window.dispatchEvent(new Event('maxed:project-saved'));
    } catch (cause) {
      setDetail((current) => current?.project.title === title ? { ...current, project: { ...current.project, title: previousTitle } } : current);
      setRenameDraft(previousTitle);
      setError(cause instanceof Error ? cause.message : 'Could not rename this project.');
    }
  }

  async function saveTitle() {
    if (!detail || !editingTitle) return;
    if (cancelTitleSave.current) {
      cancelTitleSave.current = false;
      setEditingTitle(false);
      return;
    }
    setEditingTitle(false);
    await commitTitle(titleDraft);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const text = draft.trim();
    if (!detail || !text || sending || attachmentsPending) return;
    const previous = detail;
    setDraft('');
    setSending(true);
    setError('');
    setDetail({
      ...detail,
      messages: [...detail.messages, { id: crypto.randomUUID(), role: 'user', text, createdAt: Date.now() }],
    });
    try {
      await contextQueue.current;
      const token = await getToken();
      const updated = await sendSetupMessage(token, detail.project.id, text);
      setDetail(updated);
      window.dispatchEvent(new Event('maxed:project-saved'));
    } catch (cause) {
      setDetail(previous);
      setDraft(text);
      setError(cause instanceof Error ? cause.message : 'The setup assistant did not respond.');
    } finally {
      setSending(false);
    }
  }

  async function start(mode: PracticeMode, voiceCast?: VoiceCast) {
    if (!detail?.project.ready) return;
    const sessionId = crypto.randomUUID();
    const config: InterviewConfig = {
      role: detail.project.role,
      interviewType: detail.project.interviewType,
      participants: detail.project.participants || undefined,
      difficulty: detail.project.difficulty,
      language: detail.project.language || undefined,
      voiceCast,
      mode,
      contextIds: detail.project.contextIds,
    };
    setLaunching(true);
    setError('');
    try {
      await contextQueue.current;
      const token = await getToken();
      if (mode === 'voice' && voiceCast) await updateProjectVoiceCast(token, detail.project.id, voiceCast);
      await saveSessionConfig(token, sessionId, config, true, detail.project.id);
      void trackJourney(getToken, 'practice_launched', {
        sessionId,
        properties: { mode, difficulty: config.difficulty, interviewType: config.interviewType, contextCount: config.contextIds.length },
      });
      const query = new URLSearchParams({
        role: config.role,
        interviewType: config.interviewType,
        difficulty: config.difficulty,
        mode,
        sessionId,
        projectId: detail.project.id,
      });
      if (config.language) query.set('language', config.language);
      if (config.participants) query.set('participants', config.participants);
      if (config.contextIds.length) query.set('contextIds', config.contextIds.join(','));
      navigate(`/practice?${query.toString()}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not start this practice.');
      setLaunching(false);
    }
  }

  function chooseMode(mode: PracticeMode) {
    setModeSheetOpen(false);
    if (mode === 'voice') setVoiceCastOpen(true);
    else void start(mode);
  }

  async function handleShare(event: FormEvent) {
    event.preventDefault();
    if (!detail || sharing) return;
    setSharing(true);
    setShareError('');
    setInviteUrl('');
    try {
      const token = await getToken();
      const result = await shareProject(token, detail.project.id, shareEmail, shareAccessRole);
      setShares((current) => [...current.filter((share) => share.email !== result.share.email), result.share]);
      setShareEmail('');
      if (result.share.status === 'invited') setInviteUrl(`${window.location.origin}${result.invitePath}`);
    } catch (cause) {
      setShareError(cause instanceof Error ? cause.message : 'Could not share this project.');
    } finally {
      setSharing(false);
    }
  }

  async function handleDuplicate() {
    if (!detail) return;
    try {
      const token = await getToken();
      const copy = await duplicateProject(token, detail.project.id);
      window.dispatchEvent(new Event('maxed:project-saved'));
      navigate(`/projects/${copy.project.id}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Project could not be duplicated.');
    }
  }

  async function handleDelete() {
    if (!detail || deleting) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const token = await getToken();
      await deleteProject(token, detail.project.id);
      window.dispatchEvent(new Event('maxed:project-saved'));
      navigate('/');
    } catch (cause) {
      setDeleteError(cause instanceof Error ? cause.message : 'Project could not be deleted.');
      setDeleting(false);
    }
  }

  if (loading || !detail) return <div className="panel loading-panel">{error || 'Opening practice setup...'}</div>;
  const project = detail.project;
  const completedSessions = detail.sessions.filter((session) => session.status === 'completed');
  const canLaunch = project.ready && !attachmentsPending && !contextSaving && !sending && !launching;
  const participantRoster = project.participantRoster || [];
  const attachedSources = detail.contextSources.filter((source) => project.contextIds.includes(source.id));
  const totalMinutes = completedSessions.reduce((sum, session) => sum + session.durationMinutes, 0);
  const modesUsed = [...new Set(completedSessions.map((session) => session.mode))];
  const lastPracticed = completedSessions.reduce<string | null>((latest, session) => !latest || session.createdAt > latest ? session.createdAt : latest, null);
  const tabHref = (target: ProjectTab) => target === 'setup' ? `/projects/${project.id}` : `/projects/${project.id}/${target}`;

  return (
    <div className="new-practice-page setup-chat-page">
      <header className="practice-setup-heading">
        <h1>{editingTitle
          ? <input ref={titleInputRef} className="project-title-input" aria-label="Project title" value={titleDraft} maxLength={100} onChange={(event) => setTitleDraft(event.target.value)} onBlur={() => void saveTitle()} onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); if (event.key === 'Escape') { cancelTitleSave.current = true; setTitleDraft(project.title); event.currentTarget.blur(); } }} />
          : <button type="button" className="project-title-button" onClick={editTitle} disabled={project.accessRole === 'viewer'}>{project.title}</button>}
        </h1>
        <nav className="project-tabs" aria-label="Project sections">
          <Link href={tabHref('setup')} aria-current={activeTab === 'setup' ? 'page' : undefined}>Setup</Link>
          <Link href={tabHref('sessions')} aria-current={activeTab === 'sessions' ? 'page' : undefined}>
            Sessions{!!completedSessions.length && <small>{completedSessions.length}</small>}
          </Link>
          <Link href={tabHref('settings')} aria-current={activeTab === 'settings' ? 'page' : undefined}>Settings</Link>
        </nav>
      </header>

      {activeTab === 'setup' && <>
        <section className="setup-chat" aria-label="Practice setup conversation">
          <div className="setup-chat-log" ref={chatRef} aria-live="polite">
            {detail.messages.map((message) => (
              <article key={message.id} className={`setup-message ${message.role}`}>
                <div><p>{message.text}</p></div>
              </article>
            ))}
            {sending && <article className="setup-message assistant"><div><p className="typing"><i /><i /><i /></p></div></article>}
          </div>
          <div className="setup-context-chips" ref={setContextChipsContainer} />
          <form className="setup-composer" onSubmit={(event) => void submit(event)}>
            <textarea ref={textareaRef} aria-label="Message practice setup" value={draft} maxLength={4000} rows={1} onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => { if (event.nativeEvent.isComposing) return; if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); event.currentTarget.form?.requestSubmit(); } }} />
            <div className="setup-composer-tools">
              <ContextLibrary
                projectId={project.id}
                initialIntent={searchParams.get('intent')}
                initialSources={detail.contextSources}
                selectedIds={project.contextIds}
                onSelectedIdsChange={setContextIds}
                onPendingChange={setAttachmentsPending}
                onInsertText={insertAtCursor}
                assetsContainer={contextChipsContainer}
                readOnly={project.accessRole === 'viewer'}
              />
              <button className="send-button" type="submit" disabled={!draft.trim() || sending || attachmentsPending} aria-label="Send setup message"><Send size={18} /></button>
            </div>
          </form>
          {error && <p className="error-banner">{error}</p>}
        </section>

        <section className={`practice-launch ${modeSheetOpen ? 'sheet-open' : ''}`}>
          <div className="section-heading"><div><h2>How do you want to practice?</h2>{!project.ready && <p className="launch-hint">Continue the setup conversation to unlock these options.</p>}</div></div>
          <button type="button" className="button primary launch-trigger" disabled={!canLaunch} onClick={() => setModeSheetOpen(true)}>
            {project.ready ? 'Start practice' : 'Finish setup to practice'}
          </button>
          <div className="mode-grid">
            {modes.map(({ id, title, detail: modeDetail, icon: Icon, tone }) => (
              <button key={id} className="mode-card" disabled={!canLaunch} onClick={() => chooseMode(id)}>
                <span className={`mode-icon ${tone}`}><Icon size={22} /></span>
                <span><strong>{title}</strong><small>{modeDetail}</small></span>
              </button>
            ))}
          </div>
        </section>
        {modeSheetOpen && <button className="mode-sheet-backdrop" type="button" aria-label="Close practice modes" onClick={() => setModeSheetOpen(false)} />}
      </>}

      {activeTab === 'sessions' && <section className="past-practices" aria-label="Past practices">
        {!!completedSessions.length && <dl className="sessions-summary">
          <div><dt>Practices</dt><dd>{completedSessions.length}</dd></div>
          <div><dt>Time practiced</dt><dd>{practiceTime(totalMinutes)}</dd></div>
          <div><dt>Modes used</dt><dd className="capitalize">{modesUsed.join(', ')}</dd></div>
          <div><dt>Last practice</dt><dd>{lastPracticed ? sessionDate(lastPracticed) : '—'}</dd></div>
        </dl>}
        <div className="section-heading"><h2>Past practices</h2></div>
        <div className="panel past-practice-list">
          {completedSessions.map((session: SessionSummary) => (
            <Link key={session.id} href={`/sessions/${session.id}`} className="past-practice-row">
              <span className={`session-mode ${session.mode}`}><SessionIcon mode={session.mode} /></span>
              <span><strong>{session.interviewType}</strong><small>{sessionTimestamp(session.createdAt)} · {session.durationMinutes} min</small></span>
            </Link>
          ))}
          {!completedSessions.length && <p className="empty-log">Completed practice sessions from this project will appear here. Start one from the Setup tab.</p>}
        </div>
      </section>}

      {activeTab === 'settings' && <div className="project-settings">
        <section className="panel project-settings-section">
          <h2>Project name</h2>
          <p>Shown in your project list and to anyone you share this project with.</p>
          <label className="project-settings-field">
            <span>Name</span>
            <input value={renameDraft} maxLength={100} disabled={project.accessRole === 'viewer'} onChange={(event) => setRenameDraft(event.target.value)} onBlur={() => void commitTitle(renameDraft)} onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); if (event.key === 'Escape') { setRenameDraft(project.title); event.currentTarget.blur(); } }} />
          </label>
        </section>

        <section className="panel project-settings-section">
          <h2>Meeting details</h2>
          <p>The setup assistant maintains these from your conversation on the Setup tab.</p>
          <dl className="project-facts">
            <div><dt>Meeting type</dt><dd>{project.interviewType || 'Not set yet'}</dd></div>
            <div><dt>Difficulty</dt><dd className="capitalize">{project.difficulty}</dd></div>
            <div><dt>Language</dt><dd>{project.language || 'Not set yet'}</dd></div>
            <div><dt>Participants</dt><dd>{participantRoster.length ? participantRoster.map((person) => person.name).join(', ') : 'Not set yet'}</dd></div>
            <div><dt>Created</dt><dd>{sessionDate(project.createdAt)}</dd></div>
            {!!project.role && <div className="project-fact-wide"><dt>Scenario</dt><dd>{project.role}</dd></div>}
          </dl>
        </section>

        <section className="panel project-settings-section">
          <h2>Attached materials</h2>
          <p>Documents and links the practice draws on. Add or remove them from the composer on the Setup tab.</p>
          {attachedSources.length ? <div className="project-material-list">
            {attachedSources.map((source) => <div key={source.id}><span>{source.name}</span><small>{source.status === 'ready' ? 'Ready' : source.status}</small></div>)}
          </div> : <p className="empty-log">Nothing attached yet.</p>}
        </section>

        {project.accessRole === 'owner' && <section className="panel project-settings-section">
          <h2>People with access</h2>
          <p>Viewers can use your uploaded documents. Editors can also add and remove them.</p>
          <form className="project-share-form" autoComplete="off" onSubmit={(event) => void handleShare(event)}>
            <input type="email" name="share-recipient" autoComplete="off" value={shareEmail} onChange={(event) => setShareEmail(event.target.value)} placeholder="Email address" aria-label="Email address" required />
            <select value={shareAccessRole} onChange={(event) => setShareAccessRole(event.target.value as 'viewer' | 'editor')} aria-label="Access level">
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
            </select>
            <button className="button primary" type="submit" disabled={sharing}>{sharing ? 'Sharing…' : 'Share'}</button>
          </form>
          {shareError && <p className="error-banner">{shareError}</p>}
          {inviteUrl && <div className="project-invite-result"><strong>They don’t have an account yet.</strong><span>Send this link so they can create one and join:</span><button type="button" onClick={() => void navigator.clipboard.writeText(inviteUrl)}>Copy invitation link</button></div>}
          <div className="project-share-list">
            <h3>Members</h3>
            <div><span>{user?.email}</span><small>Owner · You</small></div>
            {shares.map((share) => <div key={share.email}><span>{share.email}</span><small>{share.accessRole === 'editor' ? 'Editor' : 'Viewer'} · {share.status === 'member' ? 'Active' : 'Invitation pending'}</small></div>)}
          </div>
        </section>}

        {project.accessRole === 'owner'
          ? <section className="panel project-settings-section">
            <h2>Delete project</h2>
            <p>Removes all imported data and past practice transcripts in this project. This cannot be undone.</p>
            <button type="button" className="project-settings-action danger" onClick={() => { setDeleteError(''); setDeleteOpen(true); }}><Trash2 size={16} /> Delete project</button>
          </section>
          : <section className="panel project-settings-section">
            <h2>Duplicate project</h2>
            <p>You have {project.accessRole} access to this shared project. A copy will be yours to edit.</p>
            <button type="button" className="project-settings-action" onClick={() => void handleDuplicate()}><Copy size={16} /> Duplicate project</button>
          </section>}
        {error && <p className="error-banner">{error}</p>}
      </div>}

      {voiceCastOpen && <VoiceCastDialog
        roster={participantRoster}
        language={project.language || 'English'}
        initialCast={project.voiceCast}
        launching={launching}
        onCancel={() => setVoiceCastOpen(false)}
        onStart={(voiceCast) => void start('voice', voiceCast)}
      />}

      {deleteOpen && <div className="project-delete-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !deleting) setDeleteOpen(false); }}>
        <section className="project-delete-dialog" role="dialog" aria-modal="true" aria-labelledby="project-settings-delete-title">
          <h2 id="project-settings-delete-title">Delete “{project.title}”?</h2>
          <p>All imported data and past practice session transcripts in this project will be permanently deleted. This cannot be undone.</p>
          {deleteError && <p className="error-banner">{deleteError}</p>}
          <div className="project-delete-actions">
            <button type="button" className="button secondary" disabled={deleting} onClick={() => setDeleteOpen(false)}>Cancel</button>
            <button type="button" className="button danger" disabled={deleting} onClick={() => void handleDelete()}>{deleting ? 'Deleting...' : 'Delete project'}</button>
          </div>
        </section>
      </div>}
    </div>
  );
}
